import React, { Component } from 'react';
import { Provider } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { fetchAllContents } from 'actions/learn/contents';
import { BlankSlate } from 'components/_shared/blank_slate';
import DashboardSidebar from 'components/shared/DashboardSidebar';
import Pagination from 'components/shared/pagination';
import {
  ContentGroup,
  Spinner,
  Callout,
  Button,
  ButtonGroup,
  Modal,
  Icon,
} from 'ui';
import LinkButton from 'components/shared/LinkButton';
import { createStore } from 'stores/learn';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import { getQueryParam, setQueryStringForCurrentUrl } from 'utils/url';
import OnDemandPracticeCard from './OnDemandPracticeCard';
import FeedbackRequests from './FeedbackRequests';

export default class OnDemandPracticeRoot extends Component {
  state = {
    scenarios: [],
    loading: true,
    pagination: {},
    pageNumber: getQueryParam(window.location.href, 'page') || null,
    learnMoreModalOpen: false,
    hideCallout: false,
  };

  learnMoreButton = React.createRef();

  constructor(props) {
    super(props);
    this.store = createStore(props);
  }

  changePage = pageNumber => {
    this.setState(
      {
        pageNumber,
      },
      () => {
        setQueryStringForCurrentUrl(`?page=${this.state.pageNumber}`);
        this.getData(false);
      },
    );
  };

  getData = withSidebarRequest => {
    this.setState({
      loading: true,
    });
    const { pageNumber } = this.state;
    return request
      .get(routes.ON_DEMAND_PRACTICE.LEARNING.INDEX(pageNumber))
      .then(({ scenarios, pagination, hideCallout }) => {
        this.setState({
          scenarios,
          pagination,
          hideCallout,
          loading: withSidebarRequest,
        });
      });
  };

  updateUserBannerSettings = () => {
    const settingsData = { user_settings: { dismiss_odp_banner: 'true' } };
    request
      .post(routes.SETTINGS.UPDATE_USER_SETTINGS, settingsData)
      .then(data => {
        if (data.success) {
          this.setState({ hideCallout: true });
        }
      });
  };

  componentDidMount() {
    Promise.all([
      this.getData(true),
      this.store.dispatch(fetchAllContents()),
    ]).then(() => {
      this.setState({ loading: false });
    });
  }

  toggleLearnMoreModal = () => {
    if (this.state.learnMoreModalOpen && this.learnMoreButton.current) {
      this.learnMoreButton.current.focus();
    }
    this.setState(({ learnMoreModalOpen }) => ({
      learnMoreModalOpen: !learnMoreModalOpen,
    }));
  };

  render() {
    const { loading, scenarios, learnMoreModalOpen, hideCallout, pagination } =
      this.state;
    const onDemandPractice = this.props.meta.features.on_demand_practice;
    const isUnifiedLearn2 = this.props.meta.features.is_unified_learn2;

    const actions = (
      <div className="d-f ai-c jc-sb m-fd-c m-ai-fs">
        <Button
          ref={this.learnMoreButton}
          onClick={this.toggleLearnMoreModal}
          className="mb-16"
        >
          {t('practice.on_demand_practice.clarity.learn_more')}
        </Button>
        <LinkButton className="ta-l" onClick={this.updateUserBannerSettings}>
          {t('practice.on_demand_practice.clarity.got_it')}
        </LinkButton>
      </div>
    );
    return (
      <Provider store={this.store}>
        <div className="OnDemandPracticeRoot">
          {loading && (
            <div className="d-f ai-c jc-c">
              <Spinner className="mr-8" /> {t('common.loading')}
            </div>
          )}
          {!loading && (
            <div className="d-f m-fd-c">
              {!isUnifiedLearn2 && (
                <DashboardSidebar
                  activePage={routes.ON_DEMAND_PRACTICE.LEARNING.INDEX(1)}
                />
              )}
              <div className="OnDemandPracticeRoot-content">
                {onDemandPractice && (
                  <CSSTransition
                    classNames="CSSTransition fade"
                    in={!hideCallout}
                    timeout={500}
                    unmountOnExit
                  >
                    <Callout
                      actions={actions}
                      title={t('practice.on_demand_practice.clarity.title')}
                      className="mb-16"
                      icon="say"
                      iconClass="fz-44"
                    >
                      {t('practice.on_demand_practice.clarity.text')}
                    </Callout>
                  </CSSTransition>
                )}
                {scenarios.length > 0 && (
                  <ContentGroup
                    items={scenarios}
                    title={t('content.groups.practice')}
                  >
                    <ContentGroup.Action>
                      <a
                        className="u-linkBtn"
                        href={routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.ALL}
                      >
                        <span className="fz-bigger">
                          {t(
                            'practice.on_demand_practice.overview.learner_page.all_previous_responses.link',
                          )}
                        </span>
                        <Icon name="arrow-right" className="ml-4" />
                      </a>
                    </ContentGroup.Action>
                    <ContentGroup.Items
                      className="d-f fw-w OnDemandPracticeRoot-items"
                      render={scenario => (
                        <OnDemandPracticeCard
                          key={scenario.id}
                          scenario={scenario}
                          features={this.props.meta.features}
                        />
                      )}
                    />
                  </ContentGroup>
                )}
                {pagination.pages > 1 && (
                  <Pagination
                    pages={pagination.pages}
                    selected={pagination.selected}
                    pageSelectCallback={this.changePage}
                    customClass="mt-24 ta-c"
                  />
                )}
                {!loading && scenarios.length === 0 && (
                  <BlankSlate
                    borderStyle="dashed"
                    className="PracticeContentGroup--empty"
                    extraLarge
                  >
                    <span>{t('practice.on_demand_practice.no_responses')}</span>
                  </BlankSlate>
                )}
                <FeedbackRequests />
              </div>
            </div>
          )}
          <Modal
            activatorRef={this.learnMoreButton}
            shown={learnMoreModalOpen}
            onDismiss={this.toggleLearnMoreModal}
          >
            <Modal.Header>
              {t('practice.on_demand_practice.clarity.learn_more_modal.title')}
            </Modal.Header>
            <Modal.Content>
              <ul className="ls-n mw-text-45 fz-lg">
                <li className="d-f m-fd-c mb-32">
                  <Icon name="thumbsup" className="is-48 mr-16 mb-8" />
                  <p className="d-f fd-c mb-32 w-fill">
                    <strong>
                      {t(
                        'practice.on_demand_practice.clarity.learn_more_modal.header_one',
                      )}
                    </strong>
                    {t(
                      'practice.on_demand_practice.clarity.learn_more_modal.text_one',
                    )}
                  </p>
                </li>
                <li className="d-f m-fd-c mb-32">
                  <Icon name="bullseye" className="is-48 mr-16 mb-8" />
                  <p className="d-f fd-c mb-32 w-fill">
                    <strong>
                      {t(
                        'practice.on_demand_practice.clarity.learn_more_modal.header_two',
                      )}
                    </strong>
                    {t(
                      'practice.on_demand_practice.clarity.learn_more_modal.text_two',
                    )}
                  </p>
                </li>
                <li className="d-f m-fd-c">
                  <Icon name="trophy" className="is-48 mr-16 mb-8" />
                  <p className="d-f fd-c mb-32 w-fill">
                    <strong>
                      {t(
                        'practice.on_demand_practice.clarity.learn_more_modal.header_three',
                      )}
                    </strong>
                    {t(
                      'practice.on_demand_practice.clarity.learn_more_modal.text_three',
                    )}
                  </p>
                </li>
              </ul>
            </Modal.Content>
            <Modal.Footer>
              <ButtonGroup alignEnd>
                <Button onClick={this.toggleLearnMoreModal}>
                  {t(
                    'practice.on_demand_practice.clarity.learn_more_modal.got_it',
                  )}
                </Button>
              </ButtonGroup>
            </Modal.Footer>
          </Modal>
        </div>
      </Provider>
    );
  }
}
